import React, { useState, useEffect, useMemo } from 'react'
import toast from 'react-hot-toast'
import { HiExclamationCircle } from 'react-icons/hi'
import AlgoSymbol from 'components/AlgoSymbol'
import Button from 'components/Button'
import FilterMenu from './FilterMenu'
import MobileFilterMenu from './MobileFilterMenu'
import {
  convertMicroalgosToAlgos,
  convertAlgosToMicroalgos,
  classNames
} from 'src/helpers/utilities'
import type { PriceRange } from 'src/store/state/browseStore'

export interface PriceFilterProps {
  type: 'desktop' | 'mobile'
  minPrice: number | null
  maxPrice: number | null
  setPriceRange: (priceRange: PriceRange) => void
}

export default function PriceFilter({ type, minPrice, maxPrice, setPriceRange }: PriceFilterProps) {
  const [minPriceAlgos, setMinPriceAlgos] = useState('')
  const [maxPriceAlgos, setMaxPriceAlgos] = useState('')

  useEffect(() => {
    setMinPriceAlgos(`${minPrice ? convertMicroalgosToAlgos(minPrice) : ''}`)
    setMaxPriceAlgos(`${maxPrice ? convertMicroalgosToAlgos(maxPrice) : ''}`)
  }, [minPrice, maxPrice])

  const isDirty = useMemo(() => {
    return (
      minPriceAlgos !== `${minPrice ? convertMicroalgosToAlgos(minPrice) : ''}` ||
      maxPriceAlgos !== `${maxPrice ? convertMicroalgosToAlgos(maxPrice) : ''}`
    )
  }, [minPriceAlgos, maxPriceAlgos, minPrice, maxPrice])

  const isInvalid = useMemo(() => {
    return (
      minPriceAlgos !== '' &&
      maxPriceAlgos !== '' &&
      parseFloat(minPriceAlgos) > parseFloat(maxPriceAlgos)
    )
  }, [minPriceAlgos, maxPriceAlgos])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target

    // matches integers or floats up to 6 decimal places
    const regExp = /^\d+(?:\.\d{0,6})?$/gm

    if (value !== '' && value.match(regExp) === null) {
      return
    }

    if (name === 'minPrice') {
      setMinPriceAlgos(value)
    } else {
      setMaxPriceAlgos(value)
    }
  }

  const handleSubmit = async () => {
    const min = minPriceAlgos ? convertAlgosToMicroalgos(Number(minPriceAlgos)) : null
    const max = maxPriceAlgos ? convertAlgosToMicroalgos(Number(maxPriceAlgos)) : null

    if (isInvalid) {
      toast.error('Invalid price range', { id: 'invalid-price-range' })
      return
    }

    await setPriceRange({ minPrice: min, maxPrice: max })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSubmit()
    }
  }

  const activeCnt = [minPrice, maxPrice].reduce(
    (acc, cur) => (cur !== null ? Number(acc) + 1 : acc),
    0
  )

  const renderFilter = () => (
    <div className="space-y-4">
      <div
        className={classNames(type === 'mobile' ? 'w-full' : 'w-60', 'flex items-center space-x-2')}
      >
        <div className="flex-1">
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-500 sm:text-sm leading-normal sm:leading-normal">
                <AlgoSymbol className="text-gray-800 mr-2" />
              </span>
            </div>
            <input
              type="text"
              name="minPrice"
              id="minPrice"
              className={classNames(
                isInvalid
                  ? 'focus:text-red-700 focus:ring-red-500 focus:border-red-500 focus:pr-10'
                  : 'focus:ring-brand-500 focus:border-brand-500',
                'block w-full pl-7 border-gray-300 sm:text-sm rounded-md peer'
              )}
              value={minPriceAlgos}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder="Min"
              aria-invalid={isInvalid}
              aria-describedby="price-range-error"
            />
            <div
              className={classNames(
                isInvalid ? 'peer-focus:flex' : '',
                'hidden absolute inset-y-0 right-0 pr-3 items-center pointer-events-none'
              )}
            >
              <HiExclamationCircle className="h-5 w-5 text-red-500" aria-hidden="true" />
            </div>
          </div>
        </div>
        <div>
          <p>to</p>
        </div>
        <div className="flex-1">
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-500 sm:text-sm leading-normal sm:leading-normal">
                <AlgoSymbol className="text-gray-800 mr-2" />
              </span>
            </div>
            <input
              type="text"
              name="maxPrice"
              id="maxPrice"
              className={classNames(
                isInvalid
                  ? 'focus:text-red-700 focus:ring-red-500 focus:border-red-500 focus:pr-10'
                  : 'focus:ring-brand-500 focus:border-brand-500',
                'block w-full pl-7 border-gray-300 sm:text-sm rounded-md peer'
              )}
              value={maxPriceAlgos}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder="Max"
              aria-invalid={isInvalid}
              aria-describedby="price-range-error"
            />
            <div
              className={classNames(
                isInvalid ? 'peer-focus:flex' : '',
                'hidden absolute inset-y-0 right-0 pr-3 items-center pointer-events-none'
              )}
            >
              <HiExclamationCircle className="h-5 w-5 text-red-500" aria-hidden="true" />
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-end">
        <Button
          variant="gradient"
          className="w-full"
          onClick={handleSubmit}
          disabled={isInvalid || !isDirty}
        >
          Apply
        </Button>
      </div>
      {isInvalid && (
        <p className="mt-2 text-sm text-red-600 text-center" id="price-range-error">
          Invalid price range
        </p>
      )}
    </div>
  )

  if (type === 'mobile') {
    return <MobileFilterMenu name="Price">{renderFilter()}</MobileFilterMenu>
  }

  return (
    <FilterMenu name="Price" activeCnt={Number(activeCnt)}>
      {renderFilter()}
    </FilterMenu>
  )
}
